import { useRef } from "react";
import { useControlled } from "@mui/material/utils";
import { usePickerTranslations } from "@mui/x-date-pickers/hooks";

import { DatePicker } from "ui/date-picker/date-picker";
import { FieldLabel } from "ui/forms/field-label/field-label";
import { useFieldId } from "ui/forms/use-field-id";
import { mergeSx } from "ui/merge-sx";
import { rangeBounds, replaceInRange } from "ui/range-picker/range-picker.helpers";
import { FIELD_SX, FIELDS_SX, ROOT_SX } from "ui/range-picker/range-picker.styles";
import {
  type DateRange,
  type DateRangePosition,
  type DateRangeValidationError,
  type RangePickerProps,
} from "ui/range-picker/range-picker.types";
import { Stack } from "ui/stack/stack";

const POSITIONS: DateRangePosition[] = ["start", "end"];

const EMPTY_RANGE: DateRange = [null, null];

/**
 * Two `ui/date-picker` fields under one group label — Figma's `Date range field`, with MUI X Pro's
 * `DateRangePicker` value API. Pro itself is not used: it is commercially licensed, and Figma draws
 * two independent fields, each with its own calendar, rather than a range calendar.
 */
export const RangePicker = ({
  ref,
  id,
  name,
  label,
  required,
  disabled,
  infoTooltip,
  size = "medium",
  value: valueProp,
  defaultValue,
  onChange,
  onAccept,
  onError,
  shouldDisableDate,
  minDate,
  maxDate,
  referenceDate,
  errMessage,
  helperText,
  localeText,
  "data-testid": dataTestId,
  sx,
  ...rest
}: RangePickerProps) => {
  const groupId = useFieldId(id, name);
  const labelId = `${groupId}-label`;
  const translations = usePickerTranslations();
  const [value, setValue] = useControlled<DateRange>({
    controlled: valueProp,
    default: defaultValue ?? EMPTY_RANGE,
    name: "RangePicker",
    state: "value",
  });
  // Each field validates on its own; the range reports both results together, as Pro does.
  const validationErrors = useRef<DateRangeValidationError>([null, null]);
  const bounds = rangeBounds(value, { minDate, maxDate, referenceDate });

  return (
    <Stack
      role="group"
      aria-labelledby={label ? labelId : undefined}
      data-testid={dataTestId ?? `${groupId}-range`}
      sx={mergeSx(ROOT_SX, sx)}>
      {label && (
        <FieldLabel
          id={labelId}
          size={size}
          required={required}
          disabled={disabled}
          infoTooltip={infoTooltip}>
          {label}
        </FieldLabel>
      )}
      <Stack direction="row" sx={FIELDS_SX}>
        {POSITIONS.map((position, index) => (
          <DatePicker
            key={position}
            {...rest}
            {...bounds[position]}
            ref={position === "start" ? ref : undefined}
            id={`${groupId}-${position}`}
            name={name && `${name}-${position}`}
            label={localeText?.[position] ?? translations[position]}
            localeText={localeText}
            size={size}
            required={required}
            disabled={disabled}
            value={value[index]}
            errMessage={errMessage?.[index]}
            helperText={helperText?.[index]}
            shouldDisableDate={shouldDisableDate && ((day) => shouldDisableDate(day, position))}
            onChange={(date, context) => {
              const next = replaceInRange(value, position, date);

              validationErrors.current = replaceInRange(
                validationErrors.current,
                position,
                context.validationError,
              );
              setValue(next);
              onChange?.(next, { ...context, validationError: validationErrors.current });
            }}
            onAccept={(date, context) =>
              onAccept?.(replaceInRange(value, position, date), {
                ...context,
                validationError: replaceInRange(
                  validationErrors.current,
                  position,
                  context.validationError,
                ),
              })
            }
            onError={(error) => {
              validationErrors.current = replaceInRange(validationErrors.current, position, error);
              onError?.(validationErrors.current, value);
            }}
            sx={FIELD_SX[size]}
          />
        ))}
      </Stack>
    </Stack>
  );
};
